import React, { useState, useEffect, Fragment } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS } from './utils/adminPermissions';
import { ResponsiveTable, MobileRecruiterTokenCard, MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

const AdminRecruiterTokens = () => {
  const navigate = useNavigate();
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatingToken, setGeneratingToken] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [activeTokens, setActiveTokens] = useState([]);
  const [revokingToken, setRevokingToken] = useState(null);
  const [expandedRecruiter, setExpandedRecruiter] = useState(null);
  const [selectedRecruiters, setSelectedRecruiters] = useState(new Set());
  const [bulkGenerating, setBulkGenerating] = useState(false);

  useEffect(() => {
    fetchRecruiters();
    fetchActiveTokens();
  }, []);

  const fetchRecruiters = async () => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/recruiters`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setRecruiters(response.data);
    } catch (err) {
      console.error('Error fetching recruiters:', err);
      setError('Failed to load recruiters');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveTokens = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      const response = await axios.get(`${BACKEND_URL}/api/admin/recruiter-tokens/active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Active tokens response:', response.data);

      // Backend returns {tokens: [...], count: ...}, so extract tokens array
      const tokens = response.data?.tokens || [];
      console.log('Extracted tokens:', tokens);
      setActiveTokens(Array.isArray(tokens) ? tokens : []);
    } catch (err) {
      console.error('Error fetching active tokens:', err);
      // Set empty array on error
      setActiveTokens([]);
    }
  };

  const revokeToken = async (tokenId) => {
    try {
      setRevokingToken(tokenId);
      setError('');
      
      const token = getValidToken();
      await axios.delete(`${BACKEND_URL}/api/admin/recruiter-tokens/revoke`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: {
          token_id: tokenId
        }
      });

      // Refresh active tokens
      await fetchActiveTokens();
      
      alert('Token revoked successfully');
    } catch (err) {
      console.error('Error revoking token:', err);
      setError(
        err.response?.data?.detail || 
        'Failed to revoke token'
      );
    } finally {
      setRevokingToken(null);
    }
  };

  const generateToken = async (recruiterId) => {
    try {
      setGeneratingToken(recruiterId);
      setError('');
      
      const token = getValidToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/recruiter-tokens/generate`,
        {
          recruiter_id: recruiterId,
          expires_in_hours: 720 // 30 days
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Add the new token to our list
      setTokens(prev => [...prev, response.data]);
      
      // Refresh active tokens
      await fetchActiveTokens();
      
      // Update recruiter status
      setRecruiters(prev => prev.map(recruiter => 
        recruiter.id === recruiterId 
          ? { ...recruiter, has_token: true }
          : recruiter
      ));

      alert(`Token generated successfully!\n\nActivation URL:\n${response.data.activation_url}\n\nSend this link to ${response.data.recruiter_name} (${response.data.recruiter_email})`);

    } catch (err) {
      console.error('Error generating token:', err);
      setError(
        err.response?.data?.detail || 
        'Failed to generate token'
      );
    } finally {
      setGeneratingToken(null);
    }
  };

  const handleSelectAll = () => {
    const eligibleRecruiters = recruiters.filter(
      r => !r.password_hash && (r.approved || r.status === 'approved')
    );
    
    if (selectedRecruiters.size === eligibleRecruiters.length) {
      // Deselect all
      setSelectedRecruiters(new Set());
    } else {
      // Select all eligible
      setSelectedRecruiters(new Set(eligibleRecruiters.map(r => r.id)));
    }
  };

  const handleSelectRecruiter = (recruiterId) => {
    const newSelected = new Set(selectedRecruiters);
    if (newSelected.has(recruiterId)) {
      newSelected.delete(recruiterId);
    } else {
      newSelected.add(recruiterId);
    }
    setSelectedRecruiters(newSelected);
  };

  const generateBulkTokens = async () => {
    if (selectedRecruiters.size === 0) {
      alert('Please select at least one recruiter');
      return;
    }

    try {
      setBulkGenerating(true);
      setError('');
      
      const token = getValidToken();
      const results = [];
      const errors = [];

      // Generate tokens sequentially to avoid overwhelming the server
      for (const recruiterId of selectedRecruiters) {
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/admin/recruiter-tokens/generate`,
            {
              recruiter_id: recruiterId,
              expires_in_hours: 720 // 30 days
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          results.push(response.data);
        } catch (err) {
          const recruiter = recruiters.find(r => r.id === recruiterId);
          errors.push(`Failed to generate token for ${recruiter?.name}: ${err.response?.data?.detail || 'Unknown error'}`);
        }
      }

      // Refresh data
      await fetchActiveTokens();
      await fetchRecruiters();

      // Clear selection
      setSelectedRecruiters(new Set());

      // Show results
      if (results.length > 0) {
        const successMessage = `Successfully generated ${results.length} token(s):\n\n` +
          results.map(r => `${r.recruiter_name}: ${r.activation_url}`).join('\n\n');
        
        if (errors.length > 0) {
          alert(successMessage + '\n\nErrors:\n' + errors.join('\n'));
        } else {
          alert(successMessage);
        }
      } else if (errors.length > 0) {
        alert('Failed to generate tokens:\n' + errors.join('\n'));
      }

    } catch (err) {
      console.error('Bulk token generation error:', err);
      setError('Failed to generate bulk tokens');
    } finally {
      setBulkGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.RECRUITER_TOKENS}>
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab="recruiter-tokens"
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'students') {
            // Students button handled by grid menu
          } else if (tabId === 'recruiters') {
            // Recruiters button handled by grid menu
          } else if (tabId === 'profile') {
            // Logout functionality
            localStorage.removeItem('token');
            navigate('/admin');
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Recruiter Account Management</h1>
            <p className="mt-2 text-gray-600">
              Generate activation tokens for recruiters to set up their accounts
            </p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span className="hidden sm:inline">Back to Admin Panel</span>
            <span className="sm:hidden">Back to Admin</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-sm sm:text-lg font-medium text-gray-900">Total Recruiters</h3>
          <p className="text-2xl sm:text-3xl font-bold text-blue-600">{recruiters.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-sm sm:text-lg font-medium text-gray-900">Accounts Set Up</h3>
          <p className="text-2xl sm:text-3xl font-bold text-green-600">
            {recruiters.filter(r => r.password_hash).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-sm sm:text-lg font-medium text-gray-900">Pending Setup</h3>
          <p className="text-2xl sm:text-3xl font-bold text-orange-600">
            {recruiters.filter(r => !r.password_hash).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-sm sm:text-lg font-medium text-gray-900">Active Tokens</h3>
          <p className="text-2xl sm:text-3xl font-bold text-purple-600">{Array.isArray(activeTokens) ? activeTokens.length : 0}</p>
        </div>
      </div>

      {/* Bulk Actions */}
      {recruiters.filter(r => !r.password_hash && (r.approved || r.status === 'approved')).length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
              <h3 className="text-lg font-medium text-gray-900">Bulk Actions</h3>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                <button
                  onClick={handleSelectAll}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                >
                  <input
                    type="checkbox"
                    checked={selectedRecruiters.size > 0 && selectedRecruiters.size === recruiters.filter(r => !r.password_hash && (r.approved || r.status === 'approved')).length}
                    onChange={() => {}} // Handled by onClick
                    className="mr-2"
                  />
                  <span className="hidden sm:inline">Select All Eligible ({recruiters.filter(r => !r.password_hash && (r.approved || r.status === 'approved')).length})</span>
                  <span className="sm:hidden">Select All ({recruiters.filter(r => !r.password_hash && (r.approved || r.status === 'approved')).length})</span>
                </button>
                {selectedRecruiters.size > 0 && (
                  <span className="text-sm text-gray-600">
                    {selectedRecruiters.size} selected
                  </span>
                )}
              </div>
            </div>
            
            {selectedRecruiters.size > 0 && (
              <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                <button
                  onClick={() => setSelectedRecruiters(new Set())}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 text-center"
                >
                  Clear Selection
                </button>
                <button
                  onClick={generateBulkTokens}
                  disabled={bulkGenerating}
                  className={`px-4 py-2 text-sm font-medium rounded-md text-white ${
                    bulkGenerating
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {bulkGenerating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="hidden sm:inline">Generating Tokens...</span>
                      <span className="sm:hidden">Generating...</span>
                    </>
                  ) : (
                    <>
                      <span className="hidden sm:inline">Generate {selectedRecruiters.size} Token{selectedRecruiters.size > 1 ? 's' : ''}</span>
                      <span className="sm:hidden">Generate ({selectedRecruiters.size})</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recruiters Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Recruiters</h2>
            <button
              onClick={() => navigate('/admin')}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Recruiter Management
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-600">The token link format is <code className="bg-gray-100 px-2 py-1 rounded font-mono">/recruiter/activate?token=EnterYourTokenHere</code></p>
        </div>
        
        <ResponsiveTable 
          data={recruiters.sort((a, b) => a.organization.localeCompare(b.organization))}
          renderMobileCard={(recruiter) => (
            <MobileRecruiterTokenCard
              key={recruiter.id}
              recruiter={recruiter}
              activeTokens={activeTokens}
              isSelected={selectedRecruiters.has(recruiter.id)}
              onToggleSelect={() => handleSelectRecruiter(recruiter.id)}
              onGenerateToken={generateToken}
              generatingToken={generatingToken}
              onToggleTokens={setExpandedRecruiter}
              expandedRecruiter={expandedRecruiter}
              onRevokeToken={revokeToken}
              revokingToken={revokingToken}
            />
          )}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedRecruiters.size > 0 && selectedRecruiters.size === recruiters.filter(r => !r.password_hash && (r.approved || r.status === 'approved')).length}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recruiter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recruiters
                  .sort((a, b) => a.organization.localeCompare(b.organization))
                  .map((recruiter) => {
                  const recruiterTokens = Array.isArray(activeTokens) 
                    ? activeTokens.filter(token => token.recruiter_id === recruiter.id)
                    : [];
                  const hasTokens = recruiterTokens.length > 0;
                  const isExpanded = expandedRecruiter === recruiter.id;
                  
                  return (
                    <Fragment key={recruiter.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {!recruiter.password_hash && (recruiter.approved || recruiter.status === 'approved') ? (
                            <input
                              type="checkbox"
                              checked={selectedRecruiters.has(recruiter.id)}
                              onChange={() => handleSelectRecruiter(recruiter.id)}
                              className="rounded border-gray-300"
                            />
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {recruiter.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {recruiter.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{recruiter.organization}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        recruiter.approved || recruiter.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : recruiter.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {recruiter.status || (recruiter.approved ? 'approved' : 'pending')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        recruiter.password_hash
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {recruiter.password_hash ? 'Account Set Up' : 'Setup Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {!recruiter.password_hash && (recruiter.approved || recruiter.status === 'approved') && (
                        <button
                          onClick={() => generateToken(recruiter.id)}
                          disabled={generatingToken === recruiter.id}
                          className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white ${
                            generatingToken === recruiter.id
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          {generatingToken === recruiter.id ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Generating...
                            </>
                          ) : (
                            'Generate Token'
                          )}
                        </button>
                      )}
                      
                      {recruiter.password_hash && (
                        <span className="text-green-600 text-xs">✓ Complete</span>
                      )}
                      
                      {/* Expand button for active tokens */}
                      {hasTokens && (
                        <button
                          onClick={() => setExpandedRecruiter(isExpanded ? null : recruiter.id)}
                          className="inline-flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <svg className={`w-4 h-4 mr-1 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          {recruiterTokens.length} Token{recruiterTokens.length > 1 ? 's' : ''}
                        </button>
                      )}
                    </td>
                  </tr>
                  
                  {/* Expanded tokens row */}
                  {hasTokens && isExpanded && (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 bg-gray-50">
                        <div className="space-y-3">
                          <h4 className="font-medium text-gray-900">Active Tokens for {recruiter.name}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {recruiterTokens.map((token) => (
                              <div key={token.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900 mb-2">Token Details</div>
                                    <div className="text-xs font-mono text-gray-600 bg-gray-100 p-2 rounded mb-2 break-all">
                                      {token.token}
                                    </div>
                                    <div className="text-xs text-gray-500 space-y-1">
                                      <div>Created: {new Date(token.created_at).toLocaleDateString()}</div>
                                      <div>Expires: {new Date(token.expires_at).toLocaleDateString()}</div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => revokeToken(token.id)}
                                    disabled={revokingToken === token.id}
                                    className={`ml-4 px-3 py-2 text-sm rounded ${
                                      revokingToken === token.id
                                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                    }`}
                                  >
                                    {revokingToken === token.id ? 'Revoking...' : 'Revoke Token'}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ResponsiveTable>
      </div>

      {/* Generated Tokens */}
      {tokens.length > 0 && (
        <div className="mt-8 bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Generated Tokens</h2>
            <p className="text-sm text-gray-600">Recently generated activation tokens</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recruiter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tokens.map((token, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {token.recruiter_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {token.recruiter_email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {token.token.substring(0, 20)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(token.expires_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => copyToClipboard(token.activation_url)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Copy Link
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
    </AdminRouteGuard>
  );
};

export default AdminRecruiterTokens;